import type { TFunction } from "i18next";
import { type DateTime, Duration, Interval } from "luxon";
import type { ReactNode } from "react";
import { Trans } from "react-i18next";
import {
	AutoPostRationaleKind,
	type ExternalSource,
	type Opening,
	type OriginPerson,
	type RatioRationale,
	RequestOriginKind,
	type RequestOrigins,
	type ShiftRequest,
} from "../../../utils/models";

/** Everything the copy reads from outside a request: the language, the department's clock, its job and HR names. */
export type OriginCopyContext = {
	t: TFunction;
	locale: string;
	timezone: string;
	jobNameOf: (jobId: string) => string | null;
	hrisLabelOf: (source: ExternalSource) => string;
	/** A person as the department orders names (`name_format_last_first`) — roster staff by id, anyone else as sent. */
	personName: (person: OriginPerson) => string;
};

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
const HPPD_DECIMALS = 2;
const BOLD = { b: <b /> };
const AT_FORMAT = "ccc, LLL d · h:mm a";
const SHIFT_START_FORMAT = "ccc h:mm a";
const SHIFT_END_FORMAT = "h:mm a";
const WEEKDAY_FORMAT = "cccc";

const local = (dt: DateTime, ctx: OriginCopyContext): DateTime => dt.setZone(ctx.timezone).setLocale(ctx.locale);

/** Whole days and hours, or hours and minutes under a day — the precision a lead time is read at. */
const leadParts = (minutes: number): Record<string, number> => {
	const total = Math.abs(minutes);
	const days = Math.floor(total / MINUTES_PER_DAY);
	const hours = Math.floor((total % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
	if (days > 0) return hours > 0 ? { days, hours } : { days };
	const rest = total % MINUTES_PER_HOUR;
	if (hours === 0) return { minutes: rest };
	return rest > 0 ? { hours, minutes: rest } : { hours };
};

/** Unit by unit and joined with a space: a conjunction list ("3 days, 11 hr") reads as two figures, not one span. */
const duration = (minutes: number, ctx: OriginCopyContext): string =>
	Object.entries(leadParts(minutes))
		.map(([unit, value]) => Duration.fromObject({ [unit]: value }, { locale: ctx.locale }).toHuman({ unitDisplay: "short" }))
		.join(" ");

const shiftLabel = (request: ShiftRequest, ctx: OriginCopyContext): string =>
	`${local(request.message.shift_start, ctx).toFormat(SHIFT_START_FORMAT)}–${local(request.message.shift_end, ctx).toFormat(SHIFT_END_FORMAT)}`;

export const ratioRoleLabel = (rationale: RatioRationale, ctx: OriginCopyContext): string => {
	const jobName = rationale.jobId === null ? null : ctx.jobNameOf(rationale.jobId);
	// DEFAULT: a job no longer on the department is named by the roles it counts, the only other name it has.
	return jobName !== null ? jobName : rationale.roles.join(", ");
};

export const formatAt = (dt: DateTime, ctx: OriginCopyContext): string => local(dt, ctx).toFormat(AT_FORMAT);

export const leadFact = (minutes: number, ctx: OriginCopyContext): string =>
	ctx.t(minutes >= 0 ? "request.origin.fact.leadBefore" : "request.origin.fact.leadAfter", { duration: duration(minutes, ctx) });

export const leadShort = (minutes: number, ctx: OriginCopyContext): string =>
	ctx.t(minutes >= 0 ? "request.origin.fact.leadShortBefore" : "request.origin.fact.leadShortAfter", { duration: duration(minutes, ctx) });

export const formatHppd = (value: number): string => value.toFixed(HPPD_DECIMALS);

export const kindTitle = (opening: Opening, ctx: OriginCopyContext): string => {
	const { t } = ctx;
	switch (opening.kind) {
		case RequestOriginKind.TimeOffHris:
			return t("request.origin.kind.time_off_hris", { hris: ctx.hrisLabelOf(opening.hrisSource) });
		case RequestOriginKind.Agency:
			return t("request.origin.kind.agency", { agency: opening.agencyName });
		case RequestOriginKind.AutoPost:
			if (opening.rationale === null) return t("request.origin.kind.auto_post");
			return opening.rationale.kind === AutoPostRationaleKind.Hppd ? t("request.origin.kind.auto_post_hppd") : t("request.origin.kind.auto_post_ratio");
		case RequestOriginKind.PostedByPerson:
		case RequestOriginKind.CountRaised:
			return opening.actor === null
				? t(`request.origin.kind.${opening.kind}_anonymous`)
				: t(`request.origin.kind.${opening.kind}`, { name: ctx.personName(opening.actor) });
		case RequestOriginKind.CallOff:
		case RequestOriginKind.ReplaceRequest:
		case RequestOriginKind.SwapRequest:
		case RequestOriginKind.TimeOff:
		case RequestOriginKind.Deactivation:
		case RequestOriginKind.FromRecommendation:
		case RequestOriginKind.PostedByAnnie:
		case RequestOriginKind.AgencyCanceled:
			return t(`request.origin.kind.${opening.kind}`);
		default: {
			const unhandled: never = opening;
			throw new Error(`Unhandled origin ${JSON.stringify(unhandled)}`);
		}
	}
};

const autoPostSentence = (
	opening: Extract<Opening, { kind: RequestOriginKind.AutoPost }>,
	request: ShiftRequest,
	short: boolean,
	ctx: OriginCopyContext,
): ReactNode => {
	const rationale = opening.rationale;
	if (rationale === null) return ctx.t("request.origin.sentence.auto_post");
	if (rationale.kind === AutoPostRationaleKind.Ratio) {
		const values = {
			location: rationale.location,
			required: rationale.required,
			role: ratioRoleLabel(rationale, ctx),
			scheduled: rationale.scheduled,
			posted: rationale.alreadyPosted,
		};
		return <Trans i18nKey="request.origin.sentence.auto_post_ratio" values={values} components={BOLD} />;
	}
	const values = {
		day: local(request.message.shift_start, ctx).toFormat(WEEKDAY_FORMAT),
		scheduled: formatHppd(rationale.scheduledHppd),
		target: formatHppd(rationale.targetHppd),
	};
	return (
		<Trans i18nKey={short ? "request.origin.sentence.auto_post_hppd_short" : "request.origin.sentence.auto_post_hppd"} values={values} components={BOLD} />
	);
};

/** One cause as a sentence. `short` is the one-line form a step of the timeline uses. */
export const sentence = (opening: Opening, request: ShiftRequest, short: boolean, ctx: OriginCopyContext): ReactNode => {
	switch (opening.kind) {
		case RequestOriginKind.CallOff: {
			const values = { nurse: ctx.personName(opening.nurse), shift: shiftLabel(request, ctx), channel: ctx.t(`request.origin.channel.${opening.channel}`) };
			return <Trans i18nKey={short ? "request.origin.sentence.call_off_short" : "request.origin.sentence.call_off"} values={values} components={BOLD} />;
		}
		case RequestOriginKind.TimeOff:
		case RequestOriginKind.TimeOffHris: {
			const dates = Interval.fromDateTimes(local(opening.firstDay, ctx), local(opening.lastDay, ctx)).toLocaleString({ month: "short", day: "numeric" });
			const hris = opening.kind === RequestOriginKind.TimeOffHris ? ctx.hrisLabelOf(opening.hrisSource) : undefined;
			const anonymous = opening.kind === RequestOriginKind.TimeOff && opening.actor === null;
			const key = `request.origin.sentence.${opening.kind}${anonymous ? "_anonymous" : ""}`;
			return (
				<Trans
					i18nKey={key}
					values={{ nurse: ctx.personName(opening.nurse), actor: opening.actor ? ctx.personName(opening.actor) : undefined, dates, hris }}
					components={BOLD}
				/>
			);
		}
		case RequestOriginKind.Deactivation: {
			const key = opening.actor === null ? "request.origin.sentence.deactivation_anonymous" : "request.origin.sentence.deactivation";
			return (
				<Trans
					i18nKey={key}
					values={{ nurse: ctx.personName(opening.nurse), actor: opening.actor ? ctx.personName(opening.actor) : undefined }}
					components={BOLD}
				/>
			);
		}
		case RequestOriginKind.AutoPost:
			return autoPostSentence(opening, request, short, ctx);
		case RequestOriginKind.Agency:
			return <Trans i18nKey="request.origin.sentence.agency" values={{ agency: opening.agencyName }} components={BOLD} />;
		case RequestOriginKind.AgencyCanceled:
			return (
				<Trans
					i18nKey="request.origin.sentence.agency_canceled"
					values={{ nurse: ctx.personName(opening.nurse), agency: opening.agencyName }}
					components={BOLD}
				/>
			);
		case RequestOriginKind.CountRaised:
			return ctx.t("request.origin.sentence.count_raised", { from: opening.from, to: opening.to });
		case RequestOriginKind.ReplaceRequest:
		case RequestOriginKind.SwapRequest:
		case RequestOriginKind.PostedByPerson:
		case RequestOriginKind.FromRecommendation:
		case RequestOriginKind.PostedByAnnie: {
			const key = opening.actor === null ? `request.origin.sentence.${opening.kind}_anonymous` : `request.origin.sentence.${opening.kind}`;
			return (
				<Trans i18nKey={key} values={{ name: opening.actor ? ctx.personName(opening.actor) : undefined, shift: shiftLabel(request, ctx) }} components={BOLD} />
			);
		}
		default: {
			const unhandled: never = opening;
			throw new Error(`Unhandled origin ${JSON.stringify(unhandled)}`);
		}
	}
};

export const slotsOpened = (origins: RequestOrigins): number => origins.openings.reduce((sum, opening) => sum + opening.slotsAdded, 0);

export const cardTitle = (origins: RequestOrigins, ctx: OriginCopyContext): string => {
	const count = origins.openings.length;
	if (count === 1) return kindTitle(origins.openings[0], ctx);
	const allCallOffs = origins.openings.every((opening) => opening.kind === RequestOriginKind.CallOff);
	return ctx.t(allCallOffs ? "request.origin.title.callOffs" : "request.origin.title.causes", { count });
};

export const eyebrow = (origins: RequestOrigins, ctx: OriginCopyContext): string => ctx.t("request.origin.eyebrow", { count: slotsOpened(origins) });

/** The first cause names the slots it opened; every later one, the slots it added. */
export const slotPill = (opening: Opening, index: number, ctx: OriginCopyContext): string =>
	index === 0 ? ctx.t("request.origin.slots", { count: opening.slotsAdded }) : ctx.t("request.origin.added", { count: opening.slotsAdded });

/** "3 slots opened · 2 still open" — two counts, each with its own plural. */
export const slotsFooter = (origins: RequestOrigins, stillOpen: number, ctx: OriginCopyContext): string => {
	const opened = slotsOpened(origins);
	return `${ctx.t("request.origin.footer.opened", { count: opened })} · ${ctx.t("request.origin.footer.open", { count: stillOpen })}`;
};
